/**
 * Base API configuration and utilities
 * All API calls will be proxied through Vite dev server to Azure Functions
 */
import { ErrorResponseDto } from "@/types/api/common";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * Backend error response structure
 * @deprecated Use ErrorResponseDto from @/types/api/common instead
 */
export interface BackendErrorResponse {
    error?: string;
    message?: string;
    entityId?: number;
    entityType?: string;
    code?: string;
    numericCode?: number;
    exceptionId?: number;
}

export interface ApiError {
    message: string;
    status: number;
    details?: unknown;
}

/**
 * Extract error message from backend response
 * Returns the message or error field, or falls back to the provided fallback
 */
function extractErrorMessage(errorData: BackendErrorResponse, fallback: string): string {
    return errorData.message || errorData.error || fallback;
}

/**
 * API Exception with full error response details
 *
 * @property status - HTTP status code
 * @property errorResponse - Full structured error response from the API (if available)
 * @property details - Raw error details (legacy support)
 */
export class ApiException extends Error {
    status: number;
    /** Full structured error response from the API */
    errorResponse?: ErrorResponseDto;
    /** @deprecated Use errorResponse instead */
    details?: unknown;

    constructor(message: string, status: number, errorResponse?: ErrorResponseDto, details?: unknown) {
        super(message);
        this.name = "ApiException";
        this.status = status;
        this.errorResponse = errorResponse;
        this.details = details || errorResponse;
    }

    /**
     * Get the exception code from the error response
     */
    get code(): string | undefined {
        return this.errorResponse?.code;
    }

    /**
     * Get the numeric exception code from the error response
     */
    get numericCode(): number | undefined {
        return this.errorResponse?.numericCode;
    }

    /**
     * Get the entity ID from the error response
     */
    get entityId(): number | undefined {
        return this.errorResponse?.entityId;
    }

    /**
     * Get the entity type from the error response
     */
    get entityType(): string | undefined {
        return this.errorResponse?.entityType;
    }

    /**
     * Get the exception ID from the error response (for support tickets)
     */
    get exceptionId(): number | undefined {
        return this.errorResponse?.exceptionId;
    }
}

/**
 * Classification of a 401 response by its cause.
 * - "session-expired": authentication problem (expired/invalid token, missing credentials) —
 *   the user's setup is fine, a fresh token or re-login fixes it
 * - "no-organization": the user is genuinely assigned to zero organizations
 * - "other": a 401 whose cause is neither of the above (e.g. unresolved client, unknown)
 */
export type AuthErrorKind = "session-expired" | "no-organization" | "other";

const NO_ORGANIZATION_NUMERIC_CODE = 205; // AuthzNoOrganizationAssigned

// Legacy plain-text 401 bodies from backends that predate structured auth codes
const LEGACY_NO_ORG_MESSAGE = /not assigned to any organization/i;
const LEGACY_AUTH_MESSAGE = /invalid or expired token|missing authorization header|invalid authorization header format/i;

/**
 * Classify a caught error as a 401 auth failure. Returns null when the error is not a 401.
 *
 * Every handler that reacts to a 401 must go through this instead of `status === 401` —
 * an expired session and a missing organization assignment are different problems and
 * must never share UI treatment.
 */
export function classifyAuthError(error: unknown): AuthErrorKind | null {
    if (!(error instanceof ApiException) || error.status !== 401) {
        return null;
    }

    const numericCode = error.numericCode;
    if (numericCode !== undefined) {
        if (numericCode >= 100 && numericCode < 200) return "session-expired";
        if (numericCode === NO_ORGANIZATION_NUMERIC_CODE) return "no-organization";
        return "other";
    }

    // No structured body (older backend): fall back to the known plain-text messages
    if (LEGACY_NO_ORG_MESSAGE.test(error.message)) return "no-organization";
    if (LEGACY_AUTH_MESSAGE.test(error.message)) return "session-expired";
    return "other";
}

/**
 * Base fetch wrapper with error handling
 * Requires token to be passed explicitly for authenticated requests
 *
 * @param endpoint - API endpoint to call
 * @param options - Fetch options
 * @param token - Auth token for Bearer Authorization (required for authenticated endpoints)
 */
export async function apiFetch<T>(endpoint: string, options: RequestInit = {}, token?: string | null): Promise<T> {
    const url = endpoint.startsWith("http") ? endpoint : `${API_BASE_URL}${endpoint}`;

    const authHeaders: Record<string, string> = {};
    if (token) {
        authHeaders["Authorization"] = `Bearer ${token}`;
    }

    // Don't set Content-Type for FormData - let browser set it with boundary
    const isFormData = options.body instanceof FormData;
    const contentTypeHeader: Record<string, string> = isFormData ? {} : { "Content-Type": "application/json" };

    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                ...contentTypeHeader,
                ...authHeaders,
                ...options.headers,
            },
        });

        if (!response.ok) {
            // Try to get error message from response body (supports both JSON and plain text)
            let errorMessage = `API request failed: ${response.statusText}`;
            let errorResponse: ErrorResponseDto | undefined = undefined;

            const responseText = await response.text().catch(() => "");

            if (responseText) {
                try {
                    // Try parsing as JSON first - expecting ErrorResponseDto structure
                    const errorData = JSON.parse(responseText) as ErrorResponseDto;
                    errorMessage = extractErrorMessage(errorData, responseText);
                    errorResponse = errorData;
                } catch {
                    // Not JSON, use the plain text response as the error message
                    errorMessage = responseText;
                }
            }

            throw new ApiException(errorMessage, response.status, errorResponse);
        }

        // Handle empty responses (e.g., 204 No Content, DELETE responses)
        const contentLength = response.headers.get("Content-Length");
        const contentType = response.headers.get("Content-Type");

        // Return empty/default value if no content
        if (response.status === 204 || contentLength === "0") {
            return undefined as T;
        }

        // Try to parse JSON if content-type indicates JSON or is not specified
        if (!contentType || contentType.includes("application/json")) {
            const text = await response.text();
            // Return undefined for empty body, otherwise parse JSON
            if (!text || text.trim() === "") {
                return undefined as T;
            }
            return JSON.parse(text) as T;
        }

        // For non-JSON responses, return the text (caller should handle appropriately)
        return (await response.text()) as T;
    } catch (error) {
        if (error instanceof ApiException) {
            throw error;
        }
        throw new ApiException(error instanceof Error ? error.message : "Unknown error", 0, error);
    }
}
